import { useCallback, useMemo } from 'react';
import { CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useActiveFilters } from './use-active-filters';

type UseCallRecordsDataProps = {
  accessConfig: ColumnAccessConfig;
  drillDownFilters?: CallIntelTypes.DrillDownOptions;
  searchQuery: CallIntelTypes.SearchQuery;
  tableState: {
    pageConfig: { pageNumber: number; pageSize: number };
    sortType: CallIntelTypes.SortType;
  };
  isMounted: boolean;
  isDemoAccount?: boolean;
};

type ColumnAccessConfig = {
  isServiceQualityEnabled: boolean;
  hasServiceQualityAccess: boolean;
};

export const useCallRecordsData = ({
  accessConfig,
  drillDownFilters,
  searchQuery,
  tableState,
  isMounted,
}: UseCallRecordsDataProps) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { filters, updateFilterValues } = useActiveFilters();

  const { isServiceQualityEnabled, hasServiceQualityAccess } = accessConfig;

  const pageSize = tableState.pageConfig.pageSize ?? 10;

  const hasRequiredFilters = Boolean(filters.locations?.length && filters.startDate && filters.endDate);

  const requestConfig = useMemo(() => {
    const hasSearchText = !!searchQuery.search?.trim();

    const canUseRelevanceSort =
      hasSearchText && searchQuery.searchType === CallIntelTypes.SearchTypeEnum.SEARCH_BY_CONTENT;

    const safeSortType =
      tableState.sortType === CallIntelTypes.SortType.SORT_RELEVANCE && !canUseRelevanceSort
        ? CallIntelTypes.SortType.SORT_DESC
        : tableState.sortType;

    const baseRequest: CallIntelTypes.ListCallBaseRequestParams = {
      drillDownOptions: drillDownFilters,
      filters,
      searchQuery,
      sortType: safeSortType,
      includesAnalysis: [
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE,
        CallIntelTypes.IncludesAnalysis.CALL_CATEGORY,
        CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT,
        CallIntelTypes.IncludesAnalysis.FOLLOWUP_TASKS,
        CallIntelTypes.IncludesAnalysis.CALL_TAGS,
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENTS,
        ...(isServiceQualityEnabled && hasServiceQualityAccess
          ? [CallIntelTypes.IncludesAnalysis.CALL_SERVICE_QUALITY]
          : []),
      ],
      callVisibility: CallIntelTypes.CallVisibilityEnum.CALL_VISIBILITY_PUBLIC,
    };

    return CallIntelUtils.generateListCallsPayload({
      ...baseRequest,
      limit: pageSize,
    });
  }, [
    drillDownFilters,
    filters,
    searchQuery,
    tableState.sortType,
    isServiceQualityEnabled,
    hasServiceQualityAccess,
    pageSize,
  ]);

  const handleError = useCallback(() => {
    if (filters.hideNonAnalyzedCalls) {
      alert.error(t('Failed to hide calls with no analysis. Please try again.'));
      updateFilterValues({ hideNonAnalyzedCalls: false });
    } else {
      alert.error(t('Failed to fetch call records'));
    }
  }, [filters.hideNonAnalyzedCalls, alert, t, updateFilterValues]);

  const listCallsQuery = CallIntelligenceV2.Queries.useListCallsQuery({
    request: requestConfig,
    options: {
      enabled: isMounted && hasRequiredFilters,
      onError: handleError,
      select: (response) => {
        return {
          ...response,
          pages: response.pages.map((page) => ({
            ...page,
            rows: page.newCalls?.slice(0, pageSize) || [], // Trim extra calls
            hasNextPage: !!page.nextCursor?.id,
          })),
        };
      },
    },
  });

  return {
    listCallsQuery,
    listCallsPayload: requestConfig,
  };
};
